import React, { useEffect, useState, } from "react";
import { useLocation, useParams } from "react-router-dom";
import Header from "ui/components/shared/Header";
import { useTheme, alpha } from "@mui/material/styles";
import skillIcon from 'assets/icons/skill-icon.png';
import knowledgeIcon from 'assets/icons/knowledge-icon.png';
import LearningJourney from "./LearningJourney";
import { useTranslation } from "react-i18next";

import {
  Box,
  Typography,
  CircularProgress,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { POST } from "api/index";
import ChapterSheet from "./ChapterSheet";
// 🎨 Styled Components für das Layout
const Container = styled(Box)({
  maxWidth: "1000px",
  margin: "20px auto"
});

const SectionHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: "10px 15px",
  fontSize: "1.5rem",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: "10px",
}));

interface LocationState {
  courseTitle?: string;
}


export interface ChapterData {
  id: number;
  title: string;
  learnings: { content: string }[];
  locked: boolean;
  image?: string;
}

const linkify = (text: string) => {
  const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<>"')]+[^\s<>"')\.,])/gi;

  return text.replace(urlRegex, (url) => {
    const href = url.startsWith("http") ? url : `https://${url}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};



const SkillboxLearnings = () => {
  const { courseId } = useParams(); // Holt die Kurs-ID aus der URL
  const [chapters, setChapters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation() as { state?: LocationState };
  const storedTitle = localStorage.getItem("currentCourseTitle");
  const courseTitle = location.state?.courseTitle || storedTitle || `Kurs ${courseId}`;
  const [completedChapters, setCompletedChapters] = useState<number[]>([]);
  const token = window.localStorage.getItem("currentUserToken");
  const [learningCounts, setLearningCounts] = useState<{ [key: string]: number }>({});
  const [extensions, setExtensions] = useState<{ sources: string[]; tasks: string[] }>({ sources: [], tasks: [] });
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState<number | null>(null);



  const getLearningCount = async (chapterId: number) => {
    try {
      if (!chapterId) return 0;

      const body = new URLSearchParams();
      body.append("chapter_id", chapterId.toString());

      const response = await POST("getSkillboxLearningCount", body);
      if (!response || typeof response.learning_count === "undefined") {
        return 0;
      }

      //console.log(`Learning Count für Kapitel ${chapterId}:`, response.learning_count);

      // 🆕 Update `learningCounts` State
      setLearningCounts(prev => ({ ...prev, [chapterId]: response.learning_count }));

      return response.learning_count;
    } catch (error) {
      console.error("Fehler beim Abrufen der Learnings:", error);
      return 0;
    }
  };



  const getCompletedChapters = async (token: string | null) => {
    try {
      const body = new URLSearchParams();
      if (token) body.append("userToken", token);

      const response = await POST("getCompletedChapters", body);
      if (!response || !response.completed_chapters) {
        console.warn(" Keine abgeschlossenen Kapitel gefunden.");
        return [];
      }
      const completedChapterIds = response.completed_chapters.map(
        (chapter: { chapter_id: number }) => chapter.chapter_id
      );
      setCompletedChapters(completedChapterIds);
      return completedChapterIds;
    } catch (error) {
      console.error(" Fehler beim Abrufen der abgeschlossenen Kapitel:", error);
      return [];
    }
  };

// SkillboxLearnings
const journeyChapters = React.useMemo(() => {
  const list = [...chapters];

  if (extensions.sources.length) {
    list.push({
      id: -1,
      title : t('Wissen erweitern'),
      image : knowledgeIcon,
      locked: false,
      learnings: extensions.sources.map(src => ({ content: src })),   
    });
  }
  if (extensions.tasks.length) {
    list.push({
      id: -2,
      title : t('Skill umsetzen'),
      image : skillIcon,
      locked: false,
      learnings: extensions.tasks.map(tsk => ({ content: tsk })),     
    });
  }
  return list;
}, [chapters, extensions, t]);



  useEffect(() => {
    const fetchLearnings = async () => {
      try {
        // Kapitel des aktuellen Kurses abrufen
        const chaptersRes = await fetch("https://api.diadibi.net/getChapters", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ option: courseId || "" }),
        });
        const chaptersData = await chaptersRes.json();
        if (!chaptersData.result) return;

        // Abgeschlossene Kapitel holen
        const completedChapterIds = await getCompletedChapters(token);
        //console.log("Abgeschlossene Kapitel:", completedChapterIds);

        // Nur Kapitel dieses Kurses nehmen & nach `ordering` sortieren
        const sortedChapters = [...chaptersData.result].sort(
          (a: any, b: any) => parseInt(a.ordering) - parseInt(b.ordering)
        );

        // Letztes abgeschlossenes Kapitel bestimmen
        let lastCompletedIndex = -1;
        sortedChapters.forEach((ch, index) => {
          if (completedChapterIds.includes(Number(ch.id))) {
            lastCompletedIndex = index;
          }
        });

        //console.log("Letztes freigeschaltetes Kapitel:", sortedChapters[lastCompletedIndex]?.id);

        // Kapitel durchgehen & automatisch bis zum letzten freischalten
        const enrichedChapters = await Promise.all(
          sortedChapters.map(async (chapter, index) => {
            const isUnlocked = index <= lastCompletedIndex;

            let learnings = [];
            if (isUnlocked) {
              const learningsRes = await fetch("https://api.diadibi.net/getSkillboxContent", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ chapter_id: chapter.id }),
              });
              const learningsData = await learningsRes.json();
              learnings = learningsData.skillbox || [];
            }
            else {
              console.log("🔒 Kapitel gesperrt:", chapter.id);
            }
            // Skillbox-Erweiterungen laden
            try {
              const extRes = await fetch("https://api.diadibi.net/getSkillboxExtensions", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ course_id: courseId || "" }),
              });
              const extData = await extRes.json();
              if (extData.extensions) {
                setExtensions({
                  sources: extData.extensions.sources || [],
                  tasks: extData.extensions.tasks || [],
                });
              }

            }
            catch (error) {
              console.error("Fehler beim Laden der Erweiterungen:", error);
            }

            let chapterImage = "";
            try {
              const chapterImageRes = await fetch("https://api.diadibi.net/getChapterImage", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ chapterId: chapter.id.toString() }),
              });
              const chapterImageData = await chapterImageRes.json();
              chapterImage = chapterImageData?.result?.image || "";
            }


            catch (error) {
              console.error("Fehler beim Laden des Kapitelbildes:", error);
            }

            return {
              ...chapter,
              learnings,
              locked: !isUnlocked,
              image: chapterImage,
            };
          })
        );


        setChapters(enrichedChapters);
        sortedChapters.forEach(chapter => {
          getLearningCount(chapter.id);
        });
      } catch (error) {
        console.error("Fehler beim Laden der Inhalte:", error);
      } finally {
        setLoading(false);
      }
    };
    //console.log(chapters)
    fetchLearnings();
  }, [courseId]);


const closeSheet = () => setActiveIdx(null);
const prev       = () => setActiveIdx(i => (i ?? 1) - 1);
const next       = () => setActiveIdx(i => (i ?? 0) + 1);

const activeChapter = activeIdx !== null ? journeyChapters[activeIdx] : null;
const hasPrev       = activeIdx !== null && activeIdx > 0;
const hasNext       = activeIdx !== null && activeIdx < journeyChapters.length - 1;

const handleSelectChapter = (id: number) => {
  const idx = journeyChapters.findIndex(c => c.id === id);
  if (idx !== -1) setActiveIdx(idx);
};

  return (
    <>
      <Header />
      <Container sx={{ marginTop: "5.5rem", overflow: 'visible' }}>
        

        {loading ? (
          <CircularProgress sx={{marginTop: "5rem"}} />
        ) : chapters.length > 0 ? (
          
          <>
          <SectionHeader>{courseTitle}</SectionHeader>
          <Box sx={{ position: 'relative', overflow: 'visible', minWidth: '30vw'}}>
                <LearningJourney
      chapters={journeyChapters}        
      completedIds={completedChapters}
      onSelectChapter={handleSelectChapter}
      courseTitle={courseTitle}

    /></Box>
<ChapterSheet
  open={activeChapter !== null}
  chapter={activeChapter}
  onClose={closeSheet}
  onPrev={hasPrev ? prev : undefined}
  onNext={hasNext ? next : undefined}
  courseTitle={courseTitle}
/>
   
          </>
        ) : (
          <Typography>Keine Kapitel gefunden.</Typography>
        )}
      </Container>
    </>

    
  );
}
export default SkillboxLearnings;
